// R7: the ONLY authoritative ball-condition value. Design units, not a score
// or medical model. Everything visual/physical is derived from value [-1, 1].
import {createMoodHistory,recordMoodHistory} from './mood-history.js';
import {AFFECTED_SCALE} from './interaction-tuning.js';
export const MOOD_REVISION = 'shelter-recovery-r10';
export const MOOD_SAVE_KEY = 'allwalks-mood-r7';
export const EVENT_DELTAS = Object.freeze({sunshine:.2,breeze:.1,together:.3,rain:-.2,quicksand:-.3,stone:-.2,warmth:.2,shelter:.1});
// R9: positive weather is more accessible; rain retains the original interval.
export const WEATHER_EXPOSURE_SECONDS = Object.freeze({sunshine:8,breeze:8,rain:10});
export const SHELTER_REST_SECONDS=6;
export const EVENT_COOLDOWNS = Object.freeze({together:12,quicksand:6,stone:1.5});
export const MOOD_STATES = Object.freeze([
 {id:'very-low',label:'Very low',description:'Heavy, slow and trembling'},
 {id:'low',label:'Low',description:'A little heavier. Take your time.'},
 {id:'balanced',label:'Balanced',description:'Moving at your own pace'},
 {id:'happy',label:'Bright',description:'Lighter steps, a quicker pace'},
 {id:'overexcited',label:'Overexcited',description:'Quick, but harder to steer'}
]);
const clamp=(n,a,b)=>Math.max(a,Math.min(b,n));
const finite=(n,f=0)=>Number.isFinite(n)?n:f;
const lerp=(a,b,t)=>a+(b-a)*t;
export function moodBand(value){
 // Threshold events from zero count immediately. No gaps or double ownership:
 // [-1,-.7], (-.7,-.2], (-.2,.2), [.2,.7), [.7,1].
 const v=clamp(Math.round(finite(value)*1e6)/1e6,-1,1);
 return v<=-.7?0:v<=-.2?1:v<.2?2:v<.7?3:4;
}
export function createMood(saved={}){
 saved=saved&&typeof saved==='object'?saved:{};
 const exposure={};
 for(const key of ['sunshine','breeze','rain']){
  const seconds=WEATHER_EXPOSURE_SECONDS[key];
  // Preserve percentage progress from R7/R8 (10s), not a free reward on load.
  const progress=clamp(finite(saved.exposure?.[key]),0,(saved.version>=9?seconds:10)-1e-6);
  exposure[key]=saved.version>=9?progress:progress*seconds/10;
 }
 const time=Math.max(0,finite(saved.time));
 const next={};for(const k of Object.keys(EVENT_COOLDOWNS))next[k]=clamp(finite(saved.next?.[k]),0,time+EVENT_COOLDOWNS[k]);
 return {value:clamp(Math.round(finite(saved.value)*1e6)/1e6,-1,1),time,exposure,next,
  shelterRest:clamp(finite(saved.shelterRest),0,SHELTER_REST_SECONDS-1e-6),quicksandInside:Boolean(saved.quicksandInside),eventId:0,lastEvent:null,history:createMoodHistory(saved.history,time,clamp(finite(saved.value),-1,1))};
}
export function applyMoodEvent(state,event,{cooldown=true}={}){
 if(!Object.hasOwn(EVENT_DELTAS,event))return false;
 if(cooldown&&EVENT_COOLDOWNS[event]&&state.time+1e-7<(state.next[event]||0))return false;
 const before=state.value;
 recordMoodHistory(state.history,state.time,before,{force:true});
 state.value=clamp(Math.round((before+EVENT_DELTAS[event])*1e6)/1e6,-1,1);
 if(EVENT_COOLDOWNS[event])state.next[event]=state.time+EVENT_COOLDOWNS[event];
 recordMoodHistory(state.history,state.time,state.value,{force:true});
 state.eventId++;state.lastEvent={id:state.eventId,type:event,time:state.time,delta:EVENT_DELTAS[event],before,after:state.value};
 return true;
}
export function updateMood(state,{dt,weather='sunshine',sheltered=false,quicksand=0,quicksandEnabled=false}={}){
 // The app passes active simulation time, never wall-clock/tab catch-up time.
 dt=clamp(finite(dt),0,.1);state.time+=dt;
 if(Object.hasOwn(state.exposure,weather)&&!(weather==='rain'&&sheltered)){
  state.exposure[weather]+=dt;
  if(state.exposure[weather]+1e-7>=WEATHER_EXPOSURE_SECONDS[weather]){
   state.exposure[weather]=Math.max(0,state.exposure[weather]-WEATHER_EXPOSURE_SECONDS[weather]);
   applyMoodEvent(state,weather,{cooldown:false});
  }
 }
 // Continuous rest under the canopy restores a low ball, without farming Bright.
 if(weather==='rain'&&sheltered&&state.value<0){
  state.shelterRest+=dt;
  if(state.shelterRest+1e-7>=SHELTER_REST_SECONDS){state.shelterRest=0;applyMoodEvent(state,'shelter',{cooldown:false});}
 }else state.shelterRest=0;
 // Entry + exit hysteresis prevents noisy boundaries or parked contact from
 // applying a debuff every frame. Leaving a chapter doesn't re-arm the zone.
 if(quicksandEnabled){
  if(!state.quicksandInside&&quicksand>=.35){state.quicksandInside=true;applyMoodEvent(state,'quicksand');}
  else if(state.quicksandInside&&quicksand<=.15)state.quicksandInside=false;
 }
 return state;
}
export function moodProfile(value){
 const band=moodBand(value),v=clamp(finite(value),-1,1);
 const p={mass:1,response:28,maxSpeed:2.6,damping:9.5,squash:1,bounce:0,scale:1,steering:1,restless:0,cracks:0,tremble:0,condition:0,...MOOD_STATES[band],band};
 if(band===0)Object.assign(p,{mass:4.6,response:8,maxSpeed:.46,damping:28,scale:AFFECTED_SCALE,cracks:1,tremble:.042,condition:1});
 if(band===1){const t=clamp((-v-.2)/.5,0,1);Object.assign(p,{mass:lerp(1.5,4.6,t),response:lerp(20,8,t),maxSpeed:lerp(1.8,.46,t),damping:lerp(14,28,t),scale:lerp(1.23,AFFECTED_SCALE,t),condition:lerp(.18,1,t)});}
 if(band===3){const t=clamp((v-.2)/.5,0,1);Object.assign(p,{mass:.8,response:30,maxSpeed:lerp(3.15,3.8,t),damping:lerp(6.0,4.3,t)});}
 if(band===4){const t=clamp((v-.7)/.3,0,1);Object.assign(p,{mass:.7,response:31,maxSpeed:lerp(4.05,4.6,t),damping:lerp(2.8,1.8,t),steering:lerp(.32,.2,t),restless:lerp(.20,.5,t)});}
 return p;
}
export function serializeMood(state){return {version:10,shelterRest:state.shelterRest,history:state.history.map(p=>({...p})),value:state.value,time:state.time,exposure:{...state.exposure},next:{...state.next},quicksandInside:state.quicksandInside};}
